// src/config/roleMenus.js
// Role-specific menu configurations

import { MENU_ITEMS, SECTIONS } from './navConfig';

// Installer (web) menu - for owners/managers
export const INSTALLER_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.leads,
    MENU_ITEMS.requestimates,
    MENU_ITEMS.scheduled,
    MENU_ITEMS.inProgress,
    MENU_ITEMS.pickupDeliver,
    MENU_ITEMS.tasks
  ],
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.today,
    MENU_ITEMS.dashboard,
    MENU_ITEMS.calendar,
    MENU_ITEMS.history,
    MENU_ITEMS.financials,
    MENU_ITEMS.time
  ],
  [SECTIONS.REFERENCE]: [
    MENU_ITEMS.customers,
    MENU_ITEMS.vehicles,
    MENU_ITEMS.services,
    MENU_ITEMS.inventory,
    MENU_ITEMS.purchasing,
    MENU_ITEMS.staff
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.automations,
    MENU_ITEMS.userManagement,
    MENU_ITEMS.profile,
    MENU_ITEMS.systemHelp
  ]
};

// Dealer (web) menu
export const DEALER_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.referrals,
    MENU_ITEMS.requests,
    MENU_ITEMS.scheduled,
    MENU_ITEMS.inProgress,
    MENU_ITEMS.pickups,
    MENU_ITEMS.reviews
  ],
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.dashboard,
    MENU_ITEMS.financials
  ],
  [SECTIONS.REFERENCE]: [
    MENU_ITEMS.staff
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.systemHelp
  ]
};

// Dealer Group menu - financials focused
export const DEALER_GROUP_MENU = {
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.financials
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.systemHelp
  ]
};

// Technician (mobile) menu - simplified
export const TECHNICIAN_MENU = {
  [SECTIONS.WORK]: [
    MENU_ITEMS.myWork,
    MENU_ITEMS.myTime,
    { ...MENU_ITEMS.leads, label: 'My Leads' },
    MENU_ITEMS.tasks
  ],
  [SECTIONS.COMPLETED]: [
    MENU_ITEMS.today
  ],
  [SECTIONS.REFERENCE]: [
    MENU_ITEMS.inventory
  ],
  [SECTIONS.SYSTEM]: [
    MENU_ITEMS.systemHelp
  ]
};

// Owner menu - same as full installer menu (owner has access to everything)
export const OWNER_MENU = INSTALLER_MENU;


/**
 * Deep clone a menu object
 */
function cloneMenu(menu) {
  const cloned = {};
  for (const section in menu) {
    cloned[section] = menu[section].map(item => ({ ...item }));
  }
  return cloned;
}

/**
 * Filter installer menu based on access level and user role
 * @param {string} access - Legacy access level ('ADMIN', 'STANDARD', 'DIRECT')
 * @param {string} userRole - Unified role ('admin', 'manager', 'technician', 'viewer')
 */
function filterInstallerMenu(access, userRole) {
  const menu = cloneMenu(INSTALLER_MENU);

  // Check if user has admin privileges (either legacy or unified role)
  const isAdmin = access === 'ADMIN' || userRole === 'admin';

  // Today and Tasks gates removed — features now available to all installers

  // Inventory: hide for viewer
  if (userRole === 'viewer') {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'inventory'
    );
  }

  // Purchasing: admin-only (hide for manager, technician, viewer)
  if (!isAdmin) {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'purchasing'
    );
  }

  // User Management only for ADMIN
  if (!isAdmin) {
    menu[SECTIONS.SYSTEM] = menu[SECTIONS.SYSTEM].filter(
      item => item.id !== 'userManagement'
    );
  }

  // Staff only for ADMIN/owner or manager
  if (!isAdmin && userRole !== 'manager') {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'staff'
    );
  }

  // Services: hide for viewer
  if (userRole === 'viewer') {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'services'
    );
  }

  return menu;
}

/**
 * Filter dealer menu based on access level and role
 * Role access:
 *   manager, admin, finance, reporting — all pages (contact blocked from everything)
 *   staff — only manager, admin, finance
 *   referrals — also requires access !== 'DIRECT'
 */
function filterDealerMenu(access, role) {
  // Contact role: no menu access
  if (role === 'contact') {
    return {};
  }

  const menu = cloneMenu(DEALER_MENU);

  // No referrals for DIRECT access
  if (access === 'DIRECT') {
    menu[SECTIONS.WORK] = menu[SECTIONS.WORK].filter(
      item => item.id !== 'referrals'
    );
  }

  // Staff only for manager, admin, finance
  if (role === 'reporting') {
    menu[SECTIONS.REFERENCE] = menu[SECTIONS.REFERENCE].filter(
      item => item.id !== 'staff'
    );
  }

  return menu;
}

/**
 * Filter technician menu based on settings
 */
function filterTechnicianMenu(access, settings) {
  const menu = cloneMenu(TECHNICIAN_MENU);

  // Today and Tasks gates removed — features now available to all technicians

  // Hide Leads if technicians don't action leads
  if (!settings.technicianCanActionLeads) {
    menu[SECTIONS.WORK] = menu[SECTIONS.WORK].filter(
      item => item.id !== 'leads'
    );
  }

  // Hide My Time if time tracking is disabled
  const companyData = JSON.parse(localStorage.getItem('companyAPI') || '{}');
  if ((companyData.time_tracking_mode || 'none') === 'none') {
    menu[SECTIONS.WORK] = menu[SECTIONS.WORK].filter(
      item => item.id !== 'myTime'
    );
  }

  return menu;
}

/**
 * Get menu for a specific role with access-based filtering
 * @param {string} userType - 'installer' or 'dealer'
 * @param {string} userRole - 'owner', 'manager', 'technician', or null (legacy)
 * @param {string} access - 'ADMIN', 'STANDARD', 'DIRECT'
 * @param {object} settings - Company-specific settings
 * @returns {object} Filtered menu by section
 */
export function getMenuForRole(userType, userRole, access, settings = {}) {
  // Normalize role to lowercase to prevent casing mismatches from server
  const role = (userRole || '').toLowerCase() || null;

  // Technician uses simplified menu
  if (userType === 'installer' && role === 'technician') {
    return filterTechnicianMenu(access, settings);
  }

  // New multi-location roles
  if (userType === 'installer' && role === 'owner') {
    return filterInstallerMenu('ADMIN', 'admin');
  }
  // Installer (admin/manager/legacy)
  if (userType === 'installer') {
    return filterInstallerMenu(access, role);
  }

  // Dealer
  if (userType === 'dealer') {
    return filterDealerMenu(access, role);
  }

  // Dealer Group
  if (userType === 'dealer_group') {
    return cloneMenu(DEALER_GROUP_MENU);
  }

  return {};
}
